import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import {
  UsersFour, ShieldCheck, PencilSimple, Plus, Key, User as UserIcon,
  Trash, Copy, LockKey, Envelope, Sparkle,
} from "@phosphor-icons/react";

const BASE_ROLES = [
  { code: "super_admin", label: "Super Admin" },
  { code: "company_admin", label: "Company Admin" },
  { code: "hr", label: "HR" },
  { code: "manager", label: "Manager" },
  { code: "finance", label: "Finance" },
  { code: "employee", label: "Employee" },
];

function copyToClipboard(text) {
  try { navigator.clipboard.writeText(text); toast.success("Copied"); }
  catch { toast.error("Copy failed"); }
}

// ---------------------------------------------------------------------------
// Roles tab
// ---------------------------------------------------------------------------
function RoleEditor({ role, catalog, onSaved, onClose }) {
  const [open, setOpen] = useState(true);
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [selected, setSelected] = useState(new Set(role?.permissions || []));
  const [busy, setBusy] = useState(false);
  const isNew = !role || role.is_system === undefined;
  const isSystem = role?.is_system;

  const toggle = (perm) => {
    const next = new Set(selected);
    if (next.has(perm)) next.delete(perm); else next.add(perm);
    setSelected(next);
  };
  const toggleGroup = (perms, on) => {
    const next = new Set(selected);
    perms.forEach((p) => { if (on) next.add(p); else next.delete(p); });
    setSelected(next);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Role name is required"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim(), description, permissions: Array.from(selected) };
      if (isNew) await api.post("/rbac/roles", body);
      else await api.patch(`/rbac/roles/${role.id}`, body);
      toast.success("Saved");
      setOpen(false); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); setOpen(v); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck size={16} weight="duotone" className="text-zenith-teal" />
            {isNew ? "New role" : isSystem ? `${name} (built-in, read-only)` : `Edit role · ${name}`}
          </DialogTitle>
        </DialogHeader>
        {!isSystem && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Role name</Label>
              <Input data-testid="role-form-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1" placeholder="e.g. Recruiter" />
            </div>
            <div>
              <Label className="text-xs">Short description</Label>
              <Input data-testid="role-form-description" value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-sm mt-1" placeholder="e.g. Handles hiring" />
            </div>
          </div>
        )}
        <div className="max-h-96 overflow-y-auto pr-1 mt-2 border-t border-zinc-100 pt-3">
          {Object.entries(catalog || {}).map(([mod, perms]) => {
            const permKeys = Object.keys(perms);
            const allOn = permKeys.every((p) => selected.has(p));
            const anyOn = permKeys.some((p) => selected.has(p));
            return (
              <div key={mod} className="mb-3">
                <div className="flex items-center justify-between border-b border-zinc-100 py-1">
                  <div className="text-xs uppercase tracking-widest font-bold text-adb-navy">{mod}</div>
                  {!isSystem && (
                    <button
                      onClick={() => toggleGroup(permKeys, !allOn)}
                      className={`text-[11px] px-2 py-0.5 rounded-sm border ${allOn ? "border-zenith-magenta text-zenith-magenta" : "border-zinc-300 text-zinc-600"}`}
                    >
                      {allOn ? "clear group" : anyOn ? "select all" : "select all"}
                    </button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-x-4 mt-2">
                  {Object.entries(perms).map(([p, label]) => (
                    <label key={p} className={`flex items-start gap-2 py-1 text-sm ${isSystem ? "cursor-default" : "cursor-pointer"}`}>
                      <Checkbox
                        data-testid={`role-perm-${p}`}
                        checked={selected.has(p)}
                        onCheckedChange={() => !isSystem && toggle(p)}
                        disabled={isSystem}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-xs font-mono-plex text-zinc-600">{p}</div>
                        <div className="text-[11px] text-zinc-500">{label}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); onClose(); }} className="rounded-sm">Close</Button>
          {!isSystem && (
            <Button onClick={save} disabled={busy} data-testid="role-save-btn" className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">
              {busy ? "Saving…" : (isNew ? "Create role" : "Save changes")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RolesTab() {
  const [roles, setRoles] = useState({ system: [], custom: [] });
  const [catalog, setCatalog] = useState({});
  const [editing, setEditing] = useState(null);   // {role} | {new:true}
  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/rbac/roles"), api.get("/rbac/permissions")]);
      setRoles(r.data);
      setCatalog(p.data.catalog);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await api.delete(`/rbac/roles/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-zenith-teal font-bold">Access model</div>
          <h2 className="font-display text-2xl font-bold text-adb-navy">Roles &amp; permissions</h2>
          <p className="text-xs text-zinc-500 mt-1">Six built-in roles plus your own custom roles. Assign one base role + any number of custom roles to a user.</p>
        </div>
        <Button data-testid="role-new-btn" onClick={() => setEditing({ new: true, permissions: [] })} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
          <Plus size={14} className="mr-1" /> New custom role
        </Button>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">Built-in roles</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {roles.system.map((r) => (
            <div key={r.id} data-testid={`role-card-${r.code}`} className="border border-zinc-200 rounded-sm bg-white p-4">
              <div className="flex items-center gap-2 font-display font-bold text-adb-navy">
                <ShieldCheck size={16} weight="duotone" className="text-zenith-teal" /> {r.name}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">System role · {r.permissions.length} permissions</div>
              <Button variant="outline" size="sm" className="rounded-sm mt-3 h-7 text-xs" onClick={() => setEditing({ ...r, is_system: true })}>
                View permissions
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">Custom roles</h3>
        {roles.custom.length === 0 ? (
          <div data-testid="roles-empty" className="border border-dashed border-zinc-200 rounded-sm p-10 text-center bg-zinc-50">
            <Sparkle size={24} weight="duotone" className="mx-auto text-zenith-magenta" />
            <div className="mt-2 text-sm font-medium text-zinc-600">No custom roles yet. Create one to grant granular permissions beyond the six defaults.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {roles.custom.map((r) => (
              <div key={r.id} data-testid={`custom-role-${r.id}`} className="border border-zinc-200 rounded-sm bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold text-adb-navy">{r.name}</div>
                    {r.description && <div className="text-xs text-zinc-500 mt-0.5">{r.description}</div>}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.permissions.slice(0, 6).map((p) => (
                        <Badge key={p} variant="outline" className="rounded-sm text-[10px] font-mono-plex">{p}</Badge>
                      ))}
                      {r.permissions.length > 6 && <Badge variant="outline" className="rounded-sm text-[10px]">+{r.permissions.length - 6} more</Badge>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button data-testid={`role-edit-${r.id}`} onClick={() => setEditing(r)} className="p-1.5 rounded-sm hover:bg-zinc-100 text-zinc-600"><PencilSimple size={14} /></button>
                    <button data-testid={`role-delete-${r.id}`} onClick={() => del(r)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-500"><Trash size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <RoleEditor
          role={editing.new ? undefined : editing}
          catalog={catalog}
          onSaved={load}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------
function InviteUserDialog({ customRoles, onInvited }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee", role_ids: [] });
  const [busy, setBusy] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);

  const submit = async () => {
    if (!form.email.trim() || !form.email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rbac/users", form);
      setTempPwd({ email: data.email, password: data.initial_password });
      toast.success("Invite sent");
      onInvited();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const close = () => {
    setOpen(false);
    setForm({ name: "", email: "", role: "employee", role_ids: [] });
    setTempPwd(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button data-testid="user-invite-btn" className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">
          <Envelope size={14} className="mr-1" /> Invite user
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Envelope size={16} weight="duotone" className="text-zenith-magenta" /> Invite a new user
          </DialogTitle>
        </DialogHeader>
        {!tempPwd ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs">Work email <span className="text-red-500">*</span></Label>
              <Input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} className="rounded-sm mt-1" placeholder="jane@company.com" />
            </div>
            <div>
              <Label className="text-xs">Base role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BASE_ROLES.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {customRoles.length > 0 && (
              <div>
                <Label className="text-xs">Additional custom roles</Label>
                <div className="mt-1 border border-zinc-200 rounded-sm p-2 max-h-32 overflow-y-auto space-y-1">
                  {customRoles.map((cr) => (
                    <label key={cr.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`invite-role-${cr.id}`}
                        checked={form.role_ids.includes(cr.id)}
                        onCheckedChange={(c) => setForm({
                          ...form,
                          role_ids: c ? [...form.role_ids, cr.id] : form.role_ids.filter((x) => x !== cr.id),
                        })}
                      />
                      {cr.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="invite-success" className="rounded-sm border border-adb-green/40 bg-adb-green/10 p-4">
            <div className="text-sm font-semibold text-adb-navy">User invited</div>
            <div className="text-xs text-zinc-600 mt-1">Share these credentials securely — the temporary password is shown only once.</div>
            <div className="mt-3 space-y-2 text-sm font-mono-plex">
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 w-16">Email:</span>
                <span>{tempPwd.email}</span>
                <button onClick={() => copyToClipboard(tempPwd.email)} className="ml-auto p-1 rounded-sm hover:bg-zinc-100"><Copy size={12} /></button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 w-16">Password:</span>
                <span className="font-semibold">{tempPwd.password}</span>
                <button data-testid="invite-copy-password" onClick={() => copyToClipboard(tempPwd.password)} className="ml-auto p-1 rounded-sm hover:bg-zinc-100"><Copy size={12} /></button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {!tempPwd ? (
            <>
              <Button variant="outline" onClick={close} className="rounded-sm">Cancel</Button>
              <Button data-testid="invite-submit" onClick={submit} disabled={busy} className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
                {busy ? "Sending…" : "Send invite"}
              </Button>
            </>
          ) : (
            <Button data-testid="invite-close" onClick={close} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function UsersTab() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({ system: [], custom: [] });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [u, r] = await Promise.all([api.get("/rbac/users"), api.get("/rbac/roles")]);
      setUsers(u.data); setRoles(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return users;
    const s = q.toLowerCase();
    return users.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s));
  }, [users, q]);

  const changeBaseRole = async (id, role) => {
    try { await api.patch(`/rbac/users/${id}`, { role }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/rbac/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPwd = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/rbac/users/${u.id}/reset`);
      window.prompt("New temporary password (copy now — not shown again):", data.temporary_password);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleCustomRole = async (u, rid) => {
    const next = (u.role_ids || []).includes(rid)
      ? (u.role_ids || []).filter((x) => x !== rid)
      : [...(u.role_ids || []), rid];
    try { await api.patch(`/rbac/users/${u.id}`, { role_ids: next }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-zenith-magenta font-bold">Team access</div>
          <h2 className="font-display text-2xl font-bold text-adb-navy">Users</h2>
          <p className="text-xs text-zinc-500 mt-1">{users.length} users · {users.filter((u) => u.active !== false).length} active</p>
        </div>
        <div className="flex gap-2">
          <Input data-testid="user-search" placeholder="Search by name / email…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm w-56" />
          <InviteUserDialog customRoles={roles.custom} onInvited={load} />
        </div>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-sm bg-white">
        <table className="hr-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Base role</th>
              <th>Custom roles</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !busy && (
              <tr><td colSpan={5} className="text-center py-6 text-zinc-500 text-sm">No users found.</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-sm bg-zenith-teal/10 text-zenith-teal-700 grid place-items-center shrink-0">
                      <UserIcon size={14} weight="duotone" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-adb-navy">{u.name || u.email}</div>
                      <div className="text-[11px] font-mono-plex text-zinc-500">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <Select value={u.role} onValueChange={(v) => changeBaseRole(u.id, v)}>
                    <SelectTrigger data-testid={`user-role-${u.id}`} className="h-7 w-40 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{BASE_ROLES.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1 max-w-xs">
                    {(u.custom_roles || []).map((cr) => (
                      <Badge key={cr.id} variant="outline" className="rounded-sm text-[10px] bg-zenith-magenta/10 border-zenith-magenta/30 text-zenith-magenta">
                        {cr.name}
                        <button onClick={() => toggleCustomRole(u, cr.id)} className="ml-1 hover:opacity-70" title="Remove">×</button>
                      </Badge>
                    ))}
                    {roles.custom.length > 0 && (
                      <Select value="" onValueChange={(rid) => toggleCustomRole(u, rid)}>
                        <SelectTrigger data-testid={`user-add-role-${u.id}`} className="h-6 w-28 rounded-sm text-[10px]"><SelectValue placeholder="+ Add" /></SelectTrigger>
                        <SelectContent>
                          {roles.custom.filter((cr) => !(u.role_ids || []).includes(cr.id)).map((cr) => (
                            <SelectItem key={cr.id} value={cr.id}>{cr.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`user-active-${u.id}`} checked={u.active !== false} onCheckedChange={() => toggleActive(u)} />
                    <span className={`text-[11px] ${u.active !== false ? "text-emerald-700" : "text-zinc-400"}`}>{u.active !== false ? "Active" : "Deactivated"}</span>
                  </div>
                </td>
                <td>
                  <button data-testid={`user-reset-${u.id}`} onClick={() => resetPwd(u)} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm inline-flex items-center gap-1">
                    <Key size={12} /> Reset password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AccessControl() {
  const { user } = useAuth();
  const allowed = ["super_admin", "company_admin", "hr"].includes(user?.role);

  if (!allowed) {
    return (
      <div className="border border-red-200 bg-red-50 text-red-700 rounded-sm p-6 text-sm">
        <LockKey size={20} weight="duotone" className="mb-2" />
        You need HR / Company Admin access to manage roles &amp; users.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">Access Control</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Users, roles &amp; permissions.</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Invite new users, decide who sees what, and craft custom roles like <span className="italic text-zenith-magenta">Recruiter</span> or <span className="italic text-zenith-magenta">Payroll Reviewer</span> when the six built-in roles aren't enough.
        </p>
      </div>
      <Tabs defaultValue="users">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="users" className="rounded-sm" data-testid="tab-users"><UsersFour size={12} className="mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="roles" className="rounded-sm" data-testid="tab-roles"><ShieldCheck size={12} className="mr-1" /> Roles &amp; permissions</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="roles" className="mt-4"><RolesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
